import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useState } from 'react';

import { SUCCESS_TOASTS } from '../../../../../../constants/toasts';
import { DealMediaTypeEnum } from '../../../../../../gql/generated/graphql';
import { useProcessingSet } from '../../../../../../hooks';
import { MediaContext, MediaContextType } from '../../../../../../hooks/useMediaCollection';
import {
  trackDocumentUploadFailed,
  trackDocumentUploaded,
} from '../../../../../../services/analytics/marketplaceEvents';
import { getApiErrorMessage } from '../../../../../../services/marketplace/errors';
import { useMarketplaceExternalId } from '../../../../../../services/marketplace/externalId';
import { useDeleteLeadByExternalIdDocumentsDocumentid } from '../../../../../../services/marketplace/generated/kubb/hooks/useDeleteLeadByExternalIdDocumentsDocumentid';
import { useDeleteLeadByExternalIdPhotosPhotoId } from '../../../../../../services/marketplace/generated/kubb/hooks/useDeleteLeadByExternalIdPhotosPhotoId';
import {
  getLeadByExternalIdDocumentsQueryKey,
  useGetLeadByExternalIdDocuments,
} from '../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalIdDocuments';
import { getLeadByExternalIdPhotosQueryKey } from '../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalIdPhotos';
import {
  getLeadByExternalIdPostAcceptanceChecklistQueryKey,
  useGetLeadByExternalIdPostAcceptanceChecklist,
} from '../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalIdPostAcceptanceChecklist';
import { usePostLeadByExternalIdDocuments } from '../../../../../../services/marketplace/generated/kubb/hooks/usePostLeadByExternalIdDocuments';
import { usePostLeadByExternalIdPhotos } from '../../../../../../services/marketplace/generated/kubb/hooks/usePostLeadByExternalIdPhotos';
import { GetLeadByExternalIdDocuments200 } from '../../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalIdDocuments';
import { GetLeadByExternalIdPhotos200 } from '../../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalIdPhotos';
import { PostLeadByExternalIdDocumentsMutationRequest } from '../../../../../../services/marketplace/generated/kubb/models/PostLeadByExternalIdDocuments';
import { PostLeadByExternalIdPhotosMutationRequest } from '../../../../../../services/marketplace/generated/kubb/models/PostLeadByExternalIdPhotos';
import { logger } from '../../../../../../services/sentry';
import { INPUT_FILE_TYPES, convertHeic } from '../../../../../../utils/media';
import { createErrorToast, createSuccessToast } from '../../../../../../utils/toast';
import { PHOTO_SLOTS } from '../../photos/constants';
import { UploadKind, findChecklistSection } from './utils';

export type MarketplaceDocument = GetLeadByExternalIdDocuments200['documents'][number];

export type DocumentStatus = 'needed' | 'uploaded' | 'onDeal';

export const isDocumentSatisfied = (status: DocumentStatus | undefined) =>
  status === 'uploaded' || status === 'onDeal';

const DOCUMENT_TO_DEAL_MEDIA: Record<string, DealMediaTypeEnum> = {
  drivers_license: DealMediaTypeEnum.FrontOfDriversLicense,
  registration: DealMediaTypeEnum.Registration,
  other: DealMediaTypeEnum.Other,
};

const PHOTO_TO_DEAL_MEDIA: Record<string, DealMediaTypeEnum> = {
  odometer: DealMediaTypeEnum.OdometerPicture,
};

const MAX_DOCUMENT_SIZE_MB = 25;
const MAX_DOCUMENT_SIZE_BYTES = MAX_DOCUMENT_SIZE_MB * 1024 * 1024;

export const useDocumentUpload = (dealId: string, options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const externalId = useMarketplaceExternalId(dealId);
  const { media, uploadImage } = useContext<MediaContextType>(MediaContext);

  const { isProcessing, beginProcessing, endProcessing } = useProcessingSet<string>();

  const queryClient = useQueryClient();
  const documentsQuery = useGetLeadByExternalIdDocuments(
    { externalId },
    { query: { enabled: enabled && !!externalId } },
  );
  const checklistQuery = useGetLeadByExternalIdPostAcceptanceChecklist(
    { externalId },
    { query: { enabled: enabled && !!externalId } },
  );
  const { mutateAsync: uploadDocumentRequest } = usePostLeadByExternalIdDocuments();
  const { mutateAsync: uploadPhotoRequest } = usePostLeadByExternalIdPhotos();
  const { mutateAsync: deleteDocumentRequest } = useDeleteLeadByExternalIdDocumentsDocumentid();
  const { mutateAsync: deletePhotoRequest } = useDeleteLeadByExternalIdPhotosPhotoId();
  const toast = useToast();

  const odometer = findChecklistSection(checklistQuery.data, 'photos')?.odometer;
  const checklistPhotos = odometer ? [{ photoType: 'odometer', ...odometer }] : [];

  const documentItems = findChecklistSection(checklistQuery.data, 'documents')?.items ?? [];
  const onDeal = new Set(media.map((item) => item.type));
  const statusByType: Partial<Record<string, DocumentStatus>> = {};
  documentItems.forEach(({ documentType, status }) => {
    const dealMedia = DOCUMENT_TO_DEAL_MEDIA[documentType];
    const satisfiedByDeal = status === 'needed' && !!dealMedia && onDeal.has(dealMedia);
    statusByType[documentType] = satisfiedByDeal ? 'onDeal' : status;
  });
  // A stale photo must be re-taken, so it stays an open todo.
  checklistPhotos.forEach(({ photoType, status }) => {
    statusByType[photoType] = status === 'uploaded' ? 'uploaded' : 'needed';
  });

  const refetch = useCallback(async () => {
    if (!externalId) return;
    const queryKeys = [
      getLeadByExternalIdPostAcceptanceChecklistQueryKey({ externalId }),
      getLeadByExternalIdDocumentsQueryKey({ externalId }),
      getLeadByExternalIdPhotosQueryKey({ externalId }),
    ];
    await Promise.all(queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  }, [externalId, queryClient]);

  const performUpload = useCallback(
    async (kind: UploadKind, key: string, file: File, dealOnlyMediaType?: DealMediaTypeEnum) => {
      const isPhoto = kind === 'photo';
      if (!beginProcessing(key)) return undefined;
      try {
        const converted = await convertHeic(file);

        // Deal-only docs (e.g. payoff) are internal, so the file goes straight to the DD deal.
        if (dealOnlyMediaType) {
          await uploadImage({
            mediaType: dealOnlyMediaType,
            dealId,
            file: converted,
            deletePrev: false,
          });
          trackDocumentUploaded(dealId, key);
          toast(createSuccessToast(SUCCESS_TOASTS.payoffDocumentUploaded));
          return true;
        }

        if (!externalId) {
          throw new Error('Missing marketplace external ID');
        }

        const uploaded = isPhoto
          ? await uploadPhotoRequest({
              externalId,
              data: {
                file: converted,
                label: PHOTO_SLOTS.find((slot) => slot.photoType === key)?.id,
                photoType: key as PostLeadByExternalIdPhotosMutationRequest['photoType'],
              },
            })
          : await uploadDocumentRequest({
              externalId,
              data: {
                file: converted,
                documentType: key as PostLeadByExternalIdDocumentsMutationRequest['documentType'],
              },
            });

        trackDocumentUploaded(dealId, key, uploaded.leadId);

        if (isPhoto) {
          // Typed photos accumulate server-side; delete priors so replace semantics hold.
          const cachedPhotos =
            queryClient.getQueryData<GetLeadByExternalIdPhotos200>(
              getLeadByExternalIdPhotosQueryKey({ externalId }),
            ) ?? [];
          await Promise.all(
            cachedPhotos
              .filter((photo) => photo.photoType === key && photo.id !== uploaded.id)
              .map((photo) =>
                deletePhotoRequest({ externalId, photoId: photo.id }).catch((deleteError) =>
                  logger.error('useDocumentUpload', 'prior photo cleanup failed', key, deleteError),
                ),
              ),
          );
        }

        // Vehicle shots are marketplace-only, no DD deal media counterpart.
        const dealMediaType = isPhoto
          ? PHOTO_TO_DEAL_MEDIA[key]
          : DOCUMENT_TO_DEAL_MEDIA[key] ?? DealMediaTypeEnum.Other;
        if (dealMediaType) {
          // DD sync is best-effort, no need to hold the row's spinner on it.
          uploadImage({
            mediaType: dealMediaType,
            dealId,
            file: converted,
            deletePrev: false,
          }).catch((dealError) =>
            logger.error(
              'useDocumentUpload',
              'DD deal media sync failed',
              dealMediaType,
              dealError,
            ),
          );
        }
        await refetch();
        return uploaded;
      } catch (error) {
        toast(
          createErrorToast({
            errorMessage: getApiErrorMessage(
              error,
              `There was an error uploading your ${isPhoto ? 'photo' : 'document'}.`,
            ),
          }),
        );
        trackDocumentUploadFailed(dealId, key, 'upload_failed');
        return undefined;
      } finally {
        endProcessing(key);
      }
    },
    [
      beginProcessing,
      dealId,
      deletePhotoRequest,
      endProcessing,
      externalId,
      queryClient,
      refetch,
      toast,
      uploadDocumentRequest,
      uploadPhotoRequest,
      uploadImage,
    ],
  );

  type UploadResult = Awaited<ReturnType<typeof performUpload>>;
  const [pendingOdometer, setPendingOdometer] = useState<{
    file: File;
    resolve: (uploaded: UploadResult) => void;
  } | null>(null);

  const upload = useCallback(
    async (kind: UploadKind, key: string, file: File, dealOnlyMediaType?: DealMediaTypeEnum) => {
      const isPhoto = kind === 'photo';
      const isAcceptedType = isPhoto
        ? file.type.startsWith('image/') && file.type !== 'image/svg+xml'
        : INPUT_FILE_TYPES.includes(file.type);
      if (!isAcceptedType) {
        toast(
          createErrorToast({
            title: 'Unsupported file type',
            description: isPhoto
              ? 'Upload a photo (JPG, PNG, HEIC).'
              : 'Upload a photo (JPG, PNG, HEIC) or a PDF.',
          }),
        );
        trackDocumentUploadFailed(dealId, key, 'unsupported_type');
        return undefined;
      }
      if (file.size >= MAX_DOCUMENT_SIZE_BYTES) {
        toast(
          createErrorToast({
            title: 'File too large',
            description: `Files must be under ${MAX_DOCUMENT_SIZE_MB}MB.`,
          }),
        );
        trackDocumentUploadFailed(dealId, key, 'file_too_large');
        return undefined;
      }

      // Odometer photos must arrive with the day-of mileage reading, so the
      // upload holds until the driver enters it in the mileage prompt.
      if (isPhoto && key === 'odometer') {
        return new Promise<UploadResult>((resolve) => {
          setPendingOdometer((previous) => {
            previous?.resolve(undefined);
            return { file, resolve };
          });
        });
      }

      return performUpload(kind, key, file, dealOnlyMediaType);
    },
    [dealId, performUpload, toast],
  );

  const confirmOdometerUpload = useCallback(async () => {
    if (!pendingOdometer) return;
    setPendingOdometer(null);
    pendingOdometer.resolve(await performUpload('photo', 'odometer', pendingOdometer.file));
  }, [pendingOdometer, performUpload]);

  const cancelOdometerUpload = useCallback(() => {
    pendingOdometer?.resolve(undefined);
    setPendingOdometer(null);
  }, [pendingOdometer]);

  const removeDocument = useCallback(
    async (documentId: string) => {
      const processingKey = `remove:${documentId}` as const;
      if (!beginProcessing(processingKey)) return;
      try {
        if (!externalId) {
          throw new Error('Missing marketplace external ID');
        }
        await deleteDocumentRequest({ externalId, documentId });
        await refetch();
      } catch (error) {
        toast(
          createErrorToast({
            errorMessage: getApiErrorMessage(error, 'There was an error removing your document.'),
          }),
        );
      } finally {
        endProcessing(processingKey);
      }
    },
    [beginProcessing, deleteDocumentRequest, endProcessing, externalId, refetch, toast],
  );

  const replaceDocument = useCallback(
    async (documentType: string, file: File) => {
      const previousIds = (documentsQuery.data?.documents ?? [])
        .filter((document) => document.documentType === documentType)
        .map((document) => document.id);
      const uploaded = await upload('document', documentType, file);
      if (typeof uploaded !== 'object') return;
      await Promise.all(previousIds.filter((id) => id !== uploaded.id).map(removeDocument));
    },
    [documentsQuery.data, removeDocument, upload],
  );

  return {
    documents: documentsQuery.data?.documents ?? [],
    checklist: checklistQuery.data,
    checklistPhotos,
    statusByType,
    statusReady: !!checklistQuery.data,
    statusError: checklistQuery.isError,
    documentsReady: !!documentsQuery.data,
    isProcessing,
    upload,
    isOdometerPromptOpen: pendingOdometer !== null,
    confirmOdometerUpload,
    cancelOdometerUpload,
    replaceDocument,
    removeDocument,
    refetch,
  };
};
